import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Zap, Wrench, Cable, Cpu, Gauge, PlugZap, LineChart, Activity,
  Mail, Phone, MessageCircle, ShieldCheck, ArrowRight, Loader2, CheckCircle2,
} from "lucide-react";

const LOGO_URL = "https://customer-assets-7cd3h4nn.emergentagent.net/job_power-energy-pro/artifacts/bcgoc9pi_upscalemedia-transformed.webp";

const SERVICES = [
  {
    icon: Cpu,
    title: "Ingeniería eléctrica",
    desc: "Servicios de ingeniería eléctrica en fase conceptual, básica y de detalle para proyectos industriales.",
    image: "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=900&q=70",
    alt: "Ingeniero eléctrico revisando planos y esquemas técnicos",
  },
  {
    icon: Zap,
    title: "Subestaciones y transformadores",
    desc: "Diseño, montaje y mantenimiento de subestaciones, apartarrayos y transformadores de potencia.",
    image: "https://images.unsplash.com/photo-1548337138-e87d889cc369?auto=format&fit=crop&w=900&q=70",
    alt: "Subestación eléctrica con transformadores de potencia",
  },
  {
    icon: Gauge,
    title: "Medidores KV y KV2C",
    desc: "Instalación y mantenimiento de medidores tipo KV y KV2C, con calibración y verificación en campo.",
    image: "https://images.unsplash.com/photo-1621905251918-48416bd8575a?auto=format&fit=crop&w=900&q=70",
    alt: "Batería de medidores eléctricos industriales",
  },
  {
    icon: Cable,
    title: "Redes de media y baja tensión",
    desc: "Operación y mantenimiento integral de redes de distribución en media y baja tensión.",
    image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?auto=format&fit=crop&w=900&q=70",
    alt: "Torres y líneas de distribución eléctrica al atardecer",
  },
  {
    icon: PlugZap,
    title: "Seccionamiento en SF₆",
    desc: "Instalación, pruebas y mantenimiento de equipos de seccionamiento aislados en gas SF₆.",
    image: "https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?auto=format&fit=crop&w=900&q=70",
    alt: "Celda de seccionamiento en gas SF₆",
  },
  {
    icon: Wrench,
    title: "Empalmes, terminales y mufas",
    desc: "Trabajos especializados en empalmes, terminales premoldeadas y mufas para cableado subterráneo y aéreo.",
    image: "https://images.unsplash.com/photo-1521790361543-f645cf042ec4?auto=format&fit=crop&w=900&q=70",
    alt: "Electricista trabajando con conductores y terminales",
  },
  {
    icon: Activity,
    title: "Monitoreo de redes",
    desc: "Monitoreo de redes de distribución y generación con equipos y protocolos industriales.",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=900&q=70",
    alt: "Tableros de monitoreo y análisis de red en tiempo real",
  },
];

const MISION = "Ofrecer soluciones de ingeniería y servicios eléctricos de vanguardia para el sector industrial y de infraestructura, garantizando calidad, seguridad y continuidad operativa a nuestros clientes.";
const VISION = "Ser la empresa líder en México en soluciones eléctricas integrales para subestaciones, redes y proyectos especializados, reconocida por la excelencia técnica de nuestro equipo y la confianza que generamos con cada trabajo entregado.";

export default function Landing() {
  const [content, setContent] = useState(null);
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "", service_interest: "", message: "", hp_field: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    axios.get(`${API_BASE}/public/landing`).then((r) => setContent(r.data)).catch(() => setContent({
      brand: { name: "POWER & ENERGY", slogan: "SOLUCIONES DE VANGUARDIA", service_bar: "SUBESTACIONES · PROYECTOS · OBRA CIVIL · GESTORÍA · UVIES · INSTALACIONES · MANTENIMIENTO", logo_url: LOGO_URL },
      contact: { email: "pablo.s@poweryenergy.com.mx", phone: "", whatsapp_number: "", contact_name: "Ing. Pablo Samuel Flores Martínez" },
    }));
  }, []);

  const brand = content?.brand || {};
  const contact = content?.contact || {};

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Falta tu nombre");
    if (!form.email.trim() && !form.phone.trim()) return toast.error("Deja un correo o teléfono");
    setSending(true);
    try {
      await axios.post(`${API_BASE}/public/prospects`, { ...form, started_at: startedAt.current });
      setSent(true);
      toast.success("¡Gracias! Nuestro equipo te contactará en breve.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "No se pudo enviar. Intenta de nuevo.";
      toast.error(msg);
    } finally { setSending(false); }
  };

  const waLink = contact.whatsapp_number
    ? `https://wa.me/${contact.whatsapp_number}?text=${encodeURIComponent("Hola, me interesa un servicio de Power & Energy")}`
    : null;

  return (
    <div className="min-h-screen bg-white text-[#0A1118]" data-testid="landing-page">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#005A91]/10">
        <div className="max-w-6xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3">
            <img src={brand.logo_url || LOGO_URL} alt="Power & Energy" className="h-10 w-auto" />
          </a>
          <nav className="hidden md:flex items-center gap-7 text-sm font-bold">
            <a href="#servicios" className="hover:text-[#005A91]">Servicios</a>
            <a href="#nosotros" className="hover:text-[#005A91]">Nosotros</a>
            <a href="#contacto" className="hover:text-[#005A91]">Contacto</a>
            <Link to="/login" data-testid="landing-login-link" className="text-slate-500 hover:text-[#005A91]">Acceso interno</Link>
          </nav>
          <a href="#cotizar" data-testid="landing-cta-top" className="inline-flex items-center gap-1.5 bg-[#2E75B5] hover:bg-[#3a88cd] text-white text-sm font-bold px-4 py-2 rounded-md">
            Solicitar cotización <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </header>

      {/* Hero */}
      <section id="top" className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-15" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1509390144018-eeaf65052242?crop=entropy&cs=srgb&fm=jpg&q=85&w=1800')",
          backgroundSize: "cover", backgroundPosition: "center",
        }} />
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white/95 to-[#005A91]/8" />
        <div className="relative max-w-6xl mx-auto px-4 lg:px-6 py-14 lg:py-24 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#4ECCC8]/15 text-[#005A91] text-[11px] font-black tracking-[0.22em] uppercase px-3 py-1.5 rounded-full">
              <ShieldCheck className="h-3.5 w-3.5" /> {brand.slogan || "SOLUCIONES DE VANGUARDIA"}
            </div>
            <h1 className="mt-5 text-4xl lg:text-6xl font-black tracking-tight leading-[1.02]">
              Energía industrial que <span className="text-[#005A91]">no se detiene.</span>
            </h1>
            <p className="mt-4 text-lg text-slate-600 max-w-lg">
              Subestaciones, proyectos, gestorías, UVIES, instalaciones y servicios eléctricos
              con ingeniería propia y ejecución en campo.
            </p>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <a href="#cotizar" data-testid="hero-cta-quote" className="inline-flex items-center gap-2 bg-[#2E75B5] hover:bg-[#3a88cd] text-white font-bold text-base px-6 h-12 rounded-md">
                Solicitar cotización <ArrowRight className="h-4 w-4" />
              </a>
              {waLink && (
                <a href={waLink} target="_blank" rel="noreferrer" data-testid="hero-cta-wa" className="inline-flex items-center gap-2 border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-bold text-base px-6 h-12 rounded-md">
                  <MessageCircle className="h-4 w-4" /> WhatsApp directo
                </a>
              )}
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="relative bg-[#005A91] rounded-2xl p-8 text-white overflow-hidden">
              <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-[#4ECCC8]/25 blur-2xl" />
              <img src={brand.logo_url || LOGO_URL} alt="" className="h-20 w-auto mb-6" style={{ filter: "brightness(0) invert(1)" }} />
              <div className="text-[10px] font-black tracking-[0.28em] text-[#4ECCC8] uppercase mb-3">Áreas de expertise</div>
              <ul className="text-sm space-y-2 font-medium">
                {["Subestaciones", "Proyectos", "Obra Civil", "Gestoría", "UVIES", "Instalaciones", "Mantenimiento"].map((s) => (
                  <li key={s} className="flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#4ECCC8]" /> {s}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        <div className="bg-[#2E75B5] text-white text-[11px] font-bold tracking-[0.16em] py-2 px-4 text-center">
          {brand.service_bar}
        </div>
      </section>

      {/* Servicios */}
      <section id="servicios" className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 lg:px-6">
          <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">Servicios</div>
          <h2 className="text-3xl lg:text-4xl font-black tracking-tight mt-2 max-w-2xl">
            Lo que hacemos por su operación eléctrica
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {SERVICES.map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.title} data-testid={`service-card-${i}`}
                     className="group bg-white border border-[#005A91]/10 rounded-lg overflow-hidden hover:border-[#4ECCC8] hover:shadow-md transition-all duration-200 flex flex-col">
                  <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                    <img src={s.image} alt={s.alt}
                         loading="lazy"
                         className="absolute inset-0 h-full w-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                         data-testid={`service-image-${i}`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#005A91]/40 via-transparent to-transparent" />
                    <div className="absolute left-4 bottom-4 h-11 w-11 rounded-md bg-white/95 text-[#005A91] flex items-center justify-center shadow-sm group-hover:bg-[#4ECCC8] group-hover:text-white transition-colors duration-200">
                      <Icon className="h-5 w-5" strokeWidth={2.3} />
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="text-lg font-bold text-[#0A1118]">{s.title}</div>
                    <div className="text-sm text-slate-600 mt-2 leading-relaxed">{s.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nosotros */}
      <section id="nosotros" className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4 lg:px-6 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">Quiénes somos</div>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight mt-2">
              Ingeniería, campo y control administrativo bajo un solo techo.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Somos una empresa mexicana especializada en servicios eléctricos e industriales.
              Combinamos ingeniería de detalle con ejecución en campo — desde subestaciones
              hasta mantenimiento de redes de media y baja tensión — con procesos documentados
              y trazabilidad completa de cada proyecto.
            </p>
          </div>
          <div className="grid gap-4">
            <div className="bg-white border-l-4 border-[#4ECCC8] rounded-md p-6 shadow-sm">
              <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">Misión</div>
              <p className="mt-2 text-slate-700 leading-relaxed">{MISION}</p>
            </div>
            <div className="bg-white border-l-4 border-[#2E75B5] rounded-md p-6 shadow-sm">
              <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">Visión</div>
              <p className="mt-2 text-slate-700 leading-relaxed">{VISION}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cotización + Contacto */}
      <section id="cotizar" className="py-16 lg:py-24 bg-[#005A91] text-white">
        <div className="max-w-6xl mx-auto px-4 lg:px-6 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#4ECCC8]">Solicitar cotización</div>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight mt-2">Cuéntanos sobre tu proyecto.</h2>
            <p className="text-white/80 mt-3 max-w-md">
              Déjanos tus datos y una descripción breve. Nuestro equipo comercial te responderá
              con una propuesta técnica y comercial a la medida.
            </p>
            <div id="contacto" className="mt-8 space-y-3 text-sm">
              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-[#4ECCC8]" /> <a href={`mailto:${contact.email}`} className="hover:underline">{contact.email}</a></div>
              {contact.phone && <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-[#4ECCC8]" /> <a href={`tel:${contact.phone}`} className="hover:underline">{contact.phone}</a></div>}
              <div className="flex items-center gap-3"><ShieldCheck className="h-4 w-4 text-[#4ECCC8]" /> Contacto: <b>{contact.contact_name}</b></div>
              {waLink && (
                <a href={waLink} target="_blank" rel="noreferrer" data-testid="contact-wa" className="mt-4 inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-5 py-2.5 rounded-md">
                  <MessageCircle className="h-4 w-4" /> Escribir por WhatsApp
                </a>
              )}
            </div>
          </div>

          <div className="bg-white text-[#0A1118] rounded-lg p-6 lg:p-8">
            {sent ? (
              <div className="text-center py-10" data-testid="prospect-thank-you">
                <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500 mb-3" />
                <div className="text-2xl font-black text-[#005A91]">¡Gracias!</div>
                <p className="text-slate-600 mt-2 max-w-sm mx-auto">
                  Recibimos tu solicitud. En las próximas horas hábiles nuestro equipo te contactará.
                </p>
                <Button onClick={() => { setSent(false); setForm({ name: "", company: "", email: "", phone: "", service_interest: "", message: "", hp_field: "" }); startedAt.current = Date.now(); }}
                        variant="outline" className="mt-6 border-[#005A91]/20 text-[#005A91]" data-testid="new-request-btn">
                  Enviar otra solicitud
                </Button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="prospect-form" className="space-y-3">
                {/* Honeypot — hidden field. Real users never fill this */}
                <div style={{ position: "absolute", left: "-9999px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }} aria-hidden="true">
                  <label>Website (no llenar)
                    <input type="text" name="website" tabIndex="-1" autoComplete="off"
                            value={form.hp_field} onChange={(e) => setForm({ ...form, hp_field: e.target.value })} />
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Nombre*</Label>
                    <Input data-testid="prospect-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1 h-11 border-[#005A91]/20" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Empresa</Label>
                    <Input data-testid="prospect-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1 h-11 border-[#005A91]/20" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Correo</Label>
                    <Input data-testid="prospect-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 h-11 border-[#005A91]/20" />
                  </div>
                  <div>
                    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Teléfono</Label>
                    <Input data-testid="prospect-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 h-11 border-[#005A91]/20" />
                  </div>
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Servicio de interés</Label>
                  <Select value={form.service_interest} onValueChange={(v) => setForm({ ...form, service_interest: v })}>
                    <SelectTrigger data-testid="prospect-service" className="mt-1 h-11 border-[#005A91]/20"><SelectValue placeholder="Elegir servicio…" /></SelectTrigger>
                    <SelectContent>
                      {SERVICES.map((s) => <SelectItem key={s.title} value={s.title}>{s.title}</SelectItem>)}
                      <SelectItem value="Otro">Otro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Mensaje</Label>
                  <Textarea data-testid="prospect-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                            placeholder="Cuéntanos sobre tu proyecto o necesidad…" className="mt-1 border-[#005A91]/20 focus-visible:ring-[#4ECCC8]" />
                </div>
                <Button type="submit" disabled={sending} data-testid="prospect-submit" className="w-full h-12 bg-[#2E75B5] hover:bg-[#3a88cd] font-bold text-base">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ArrowRight className="h-4 w-4 mr-2" />}
                  Enviar solicitud
                </Button>
                <p className="text-[11px] text-slate-500 text-center leading-relaxed">
                  Al enviar aceptas que Power & Energy te contacte por los medios indicados. No enviamos spam.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-300 py-10">
        <div className="max-w-6xl mx-auto px-4 lg:px-6 flex items-start justify-between flex-wrap gap-6">
          <div>
            <img src={brand.logo_url || LOGO_URL} alt="Power & Energy" className="h-12 w-auto" style={{ filter: "brightness(0) invert(1)" }} />
            <div className="text-[10px] font-black tracking-[0.24em] uppercase text-[#4ECCC8] mt-3">{brand.slogan}</div>
            <div className="text-xs text-slate-500 mt-2">© {new Date().getFullYear()} POWER & ENERGY. Todos los derechos reservados.</div>
          </div>
          <div className="text-sm space-y-1">
            <div className="text-[10px] font-black tracking-[0.24em] uppercase text-white/80">Contacto</div>
            <div>{contact.contact_name}</div>
            <div><a href={`mailto:${contact.email}`} className="hover:text-white">{contact.email}</a></div>
          </div>
          <div className="text-sm space-y-1">
            <div className="text-[10px] font-black tracking-[0.24em] uppercase text-white/80">Legal</div>
            <Link to="/politicas" data-testid="footer-policies-link"
                  className="hover:text-white block">
              Políticas de entrega y servicio
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
